import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Modal, StatusBadge } from "@fawkes/ui";
import { Eye, FlaskConical, MailPlus, Send } from "lucide-react";

import { formatDate } from "../i18n/format";
import { foutBody } from "./klantverzoeken/types";
import "./Klantverzoeken.css";

/**
 * De tegel "Klantportaal" op de klantkaart (ontwerp Klantportaal Pension, hoofdstuk 5 en 21): wie van deze klant
 * in het portaal kan, welke uitnodigingen openstaan, en uitnodigen, blokkeren of toegang intrekken. Blokkeren
 * raakt alleen dat ene lid; de rest van het huishouden kan gewoon verder.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

interface Lid {
  id: string;
  accountId: string;
  email: string;
  naam: string | null;
  rol: "BEHEERDER" | "GEZINSLID";
  rechtenprofiel: string;
  status: "ACTIEF" | "IN_AFWACHTING" | "GEBLOKKEERD" | "INGETROKKEN";
}

interface Uitnodiging {
  id: string;
  email: string;
  naam: string | null;
  rol: string;
  verlooptOp: string;
  status: "OPEN" | "GEACCEPTEERD" | "INGETROKKEN" | "VERLOPEN";
}

interface Huishouden {
  id: string;
  naam: string;
  status: string;
  leden: Lid[];
  uitnodigingen: Uitnodiging[];
}

const TOON = { ACTIEF: "success", IN_AFWACHTING: "warning", GEBLOKKEERD: "danger", INGETROKKEN: "neutral" } as const;

export function KlantportaalTile({ relatieId }: { relatieId: string }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const url = `klanten/${relatieId}/portaal`;
  const { data, isLoading, isError, refetch } = useCustom<Huishouden[]>({ url, method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [uitnodigen, setUitnodigen] = useState(false);
  const [bericht, setBericht] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);
  const [weergave, setWeergave] = useState<"SUPPORT" | "TEST" | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const huishoudens = Array.isArray(data?.data) ? data.data : [];
  const nu = Date.now();

  async function doe(pad: string, method: "post" | "put" | "delete", values: object = {}): Promise<boolean> {
    setFout(null);
    try {
      await mutateAsync({ url: `${url}/${pad}`, method, values, successNotification: false, errorNotification: false });
      await refetch();
      return true;
    } catch (err) {
      const code = (err as { statusCode?: number })?.statusCode;
      setFout(code === 403 ? t("klantportaalTegel.fout.recht") : foutBody(err).melding ?? t("klantportaalTegel.fout.algemeen"));
      return false;
    }
  }

  if (isLoading) {
    return <p className="kvd__stil">{t("common.loading")}</p>;
  }
  if (isError) {
    return <p className="kvd__stil">{t("klantportaalTegel.fout.laden")}</p>;
  }

  const leden = huishoudens.flatMap((h) => h.leden).filter((l) => l.status !== "INGETROKKEN");
  const open = huishoudens.flatMap((h) => h.uitnodigingen).filter((u) => u.status === "OPEN" && new Date(u.verlooptOp).getTime() > nu);

  return (
    <div className="kpt">
      {leden.length === 0 && open.length === 0 && <p className="kvd__stil">{t("klantportaalTegel.leeg")}</p>}
      {leden.length > 0 && (
        <ul className="kpt__lijst">
          {leden.map((l) => (
            <li key={l.id}>
              <span className="kpt__wie">
                <strong>{l.naam ?? l.email}</strong>
                <span className="kvd__stil">
                  {l.email} · {t(`klantportaalTegel.rol.${l.rol}`)}
                </span>
              </span>
              <StatusBadge tone={TOON[l.status]}>{t(`klantportaalTegel.status.${l.status}`)}</StatusBadge>
              <span className="kpt__knoppen">
                {l.status === "GEBLOKKEERD" ? (
                  <button type="button" className="kpt__link" disabled={bezig}
                    onClick={() => void doe(`leden/${l.id}/toegang`, "put", { status: "ACTIEF" })}>
                    {t("klantportaalTegel.deblokkeren")}
                  </button>
                ) : (
                  <button type="button" className="kpt__link kpt__link--gevaar" disabled={bezig}
                    onClick={() => void doe(`leden/${l.id}/toegang`, "put", { status: "GEBLOKKEERD" })}>
                    {t("klantportaalTegel.blokkeren")}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open.length > 0 && (
        <ul className="kpt__lijst">
          {open.map((u) => (
            <li key={u.id} className="kpt__uitnodiging">
              <span className="kpt__wie">
                <strong>{u.naam ?? u.email}</strong>
                <span className="kvd__stil">
                  {t("klantportaalTegel.uitgenodigd", { args: [u.email, formatDate(u.verlooptOp, locale)] })}
                </span>
              </span>
              <span className="kpt__knoppen">
                <button type="button" className="kpt__link kpt__link--gevaar" disabled={bezig}
                  onClick={() => void doe(`uitnodigingen/${u.id}`, "delete")}>
                  {t("klantportaalTegel.intrekken")}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {fout && !uitnodigen && !bericht && <p className="fawkes-error" role="alert">{fout}</p>}
      {verstuurd && <p className="kvd__stil" role="status">{t("klantportaalTegel.berichtVerstuurd")}</p>}
      <span className="kpt__acties">
        <Button variant="secondary" onClick={() => setUitnodigen(true)}>
          <MailPlus size={16} aria-hidden="true" /> {t("klantportaalTegel.uitnodigen")}
        </Button>
        {leden.some((l) => l.status === "ACTIEF") && (
          <Button variant="secondary" onClick={() => { setVerstuurd(false); setBericht(true); }}>
            <Send size={16} aria-hidden="true" /> {t("klantportaalTegel.berichtSturen")}
          </Button>
        )}
        {leden.some((l) => l.status === "ACTIEF") && (
          <>
            <Button variant="secondary" onClick={() => setWeergave("SUPPORT")}>
              <Eye size={16} aria-hidden="true" /> {t("klantportaalTegel.bekijkAlsKlant")}
            </Button>
            <Button variant="secondary" onClick={() => setWeergave("TEST")}>
              <FlaskConical size={16} aria-hidden="true" /> {t("klantportaalTegel.wizardTesten")}
            </Button>
          </>
        )}
      </span>
      <WeergaveVenster key={weergave ?? "dicht"} t={t} modus={weergave} bezig={bezig} fout={fout}
        leden={leden.filter((l) => l.status === "ACTIEF")}
        onClose={() => { setWeergave(null); setFout(null); }}
        onStart={async (waarden) => {
          setFout(null);
          // Eerst een leeg venster openen (nog binnen de klik), anders houdt een popupblokker het tegen.
          const venster = window.open("about:blank", "_blank");
          try {
            const antwoord = await mutateAsync({ url: `klanten/${relatieId}/portaal/weergave`, method: "post",
              values: { ...waarden, modus: weergave }, successNotification: false, errorNotification: false });
            const doel = (antwoord.data as { url?: string })?.url;
            if (venster && doel) {
              venster.opener = null;
              venster.location.href = doel;
            }
            setWeergave(null);
          } catch (err) {
            venster?.close();
            const code = (err as { statusCode?: number })?.statusCode;
            setFout(code === 403 ? t("klantportaalTegel.fout.recht") : foutBody(err).melding ?? t("klantportaalTegel.fout.algemeen"));
          }
        }} />
      <BerichtVenster key={String(bericht)} t={t} open={bericht} bezig={bezig} fout={fout}
        leden={leden.filter((l) => l.status === "ACTIEF")}
        onClose={() => { setBericht(false); setFout(null); }}
        onVerstuur={async (waarden) => {
          setFout(null);
          try {
            await mutateAsync({ url: `klanten/${relatieId}/berichten`, method: "post", values: waarden,
              successNotification: false, errorNotification: false });
            setBericht(false);
            setVerstuurd(true);
          } catch (err) {
            setFout(foutBody(err).melding ?? t("klantportaalTegel.fout.algemeen"));
          }
        }} />
      <UitnodigVenster t={t} open={uitnodigen} bezig={bezig} fout={fout} eerste={leden.length === 0}
        onClose={() => { setUitnodigen(false); setFout(null); }}
        onVerstuur={async (waarden) => { if (await doe("uitnodigingen", "post", waarden)) setUitnodigen(false); }} />
    </div>
  );
}

function UitnodigVenster({ t, open, bezig, fout, eerste, onClose, onVerstuur }: {
  t: T;
  open: boolean;
  bezig: boolean;
  fout: string | null;
  eerste: boolean;
  onClose: () => void;
  onVerstuur: (waarden: object) => void;
}) {
  const [email, setEmail] = useState("");
  const [naam, setNaam] = useState("");
  const [rol, setRol] = useState<"" | "BEHEERDER" | "GEZINSLID">("");
  return (
    <Modal open={open} title={t("klantportaalTegel.uitnodigen")} onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("klantverzoeken.annuleren")}</Button>
          <Button variant="primary" type="submit" form="kpt-uitnodigen" disabled={bezig}>{t("klantportaalTegel.versturen")}</Button>
        </>
      }>
      <form id="kpt-uitnodigen" className="kvd__form"
        onSubmit={(e) => { e.preventDefault(); onVerstuur({ email, naam, rol: rol || undefined }); }}>
        <p className="kvd__stil">{eerste ? t("klantportaalTegel.uitlegEerste") : t("klantportaalTegel.uitleg")}</p>
        <label>
          {t("klantportaalTegel.naam")}
          <input required value={naam} onChange={(e) => setNaam(e.target.value)} />
        </label>
        <label>
          {t("klantportaalTegel.email")}
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          {t("klantportaalTegel.rolKiezen")}
          <select value={rol} onChange={(e) => setRol(e.target.value as typeof rol)}>
            <option value="">{t("klantportaalTegel.rolAutomatisch")}</option>
            <option value="BEHEERDER">{t("klantportaalTegel.rol.BEHEERDER")}</option>
            <option value="GEZINSLID">{t("klantportaalTegel.rol.GEZINSLID")}</option>
          </select>
        </label>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}

/** Een bericht van het pension in het portaal: aan het hele huishouden, of persoonlijk aan één lid. */
/** Supportweergave (alleen lezen, met reden) of een testdoorloop van de wizard als een van de leden. */
function WeergaveVenster({ t, modus, bezig, fout, leden, onClose, onStart }: {
  t: T;
  modus: "SUPPORT" | "TEST" | null;
  bezig: boolean;
  fout: string | null;
  leden: Lid[];
  onClose: () => void;
  onStart: (waarden: { lidmaatschapId: string; reden: string }) => void;
}) {
  const [lid, setLid] = useState(leden[0]?.id ?? "");
  const [reden, setReden] = useState("");
  const support = modus === "SUPPORT";
  return (
    <Modal open={modus !== null} title={t(support ? "klantportaalTegel.bekijkAlsKlant" : "klantportaalTegel.wizardTesten")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("klantverzoeken.annuleren")}</Button>
          <Button variant="primary" type="submit" form="kpt-weergave" disabled={bezig}>{t("klantportaalTegel.weergaveOpenen")}</Button>
        </>
      }>
      <form id="kpt-weergave" className="kvd__form"
        onSubmit={(e) => { e.preventDefault(); onStart({ lidmaatschapId: lid, reden }); }}>
        <p className="kvd__stil">{t(support ? "klantportaalTegel.supportUitleg" : "klantportaalTegel.testUitleg")}</p>
        <label>
          {t("klantportaalTegel.weergaveAls")}
          <select value={lid} onChange={(e) => setLid(e.target.value)}>
            {leden.map((l) => (
              <option key={l.id} value={l.id}>{l.naam ?? l.email}</option>
            ))}
          </select>
        </label>
        {support && (
          <label>
            {t("klantportaalTegel.weergaveReden")}
            <textarea required rows={3} maxLength={500} value={reden} onChange={(e) => setReden(e.target.value)} />
          </label>
        )}
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}

function BerichtVenster({ t, open, bezig, fout, leden, onClose, onVerstuur }: {
  t: T;
  open: boolean;
  bezig: boolean;
  fout: string | null;
  leden: Lid[];
  onClose: () => void;
  onVerstuur: (waarden: object) => void;
}) {
  const [aan, setAan] = useState("");
  const [onderwerp, setOnderwerp] = useState("");
  const [tekst, setTekst] = useState("");
  return (
    <Modal open={open} title={t("klantportaalTegel.berichtSturen")} onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("klantverzoeken.annuleren")}</Button>
          <Button variant="primary" type="submit" form="kpt-bericht" disabled={bezig}>{t("klantportaalTegel.berichtVersturen")}</Button>
        </>
      }>
      <form id="kpt-bericht" className="kvd__form"
        onSubmit={(e) => { e.preventDefault(); onVerstuur({ onderwerp, tekst, accountId: aan || undefined }); }}>
        <p className="kvd__stil">{t("klantportaalTegel.berichtUitleg")}</p>
        <label>
          {t("klantportaalTegel.berichtAan")}
          <select value={aan} onChange={(e) => setAan(e.target.value)}>
            <option value="">{t("klantportaalTegel.berichtHuishouden")}</option>
            {leden.map((l) => (
              <option key={l.accountId} value={l.accountId}>
                {t("klantportaalTegel.berichtPersoonlijk", { args: [l.naam ?? l.email] })}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("klantportaalTegel.berichtOnderwerp")}
          <input required maxLength={200} value={onderwerp} onChange={(e) => setOnderwerp(e.target.value)} />
        </label>
        <label>
          {t("klantportaalTegel.berichtTekst")}
          <textarea required rows={5} maxLength={5000} value={tekst} onChange={(e) => setTekst(e.target.value)} />
        </label>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
      </form>
    </Modal>
  );
}
